import { useState, useEffect, useRef, useCallback } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';

// League logo URLs from ESPN
const sportLogos = {
  'nfl': 'https://a.espncdn.com/i/teamlogos/leagues/500/nfl.png',
  'nba': 'https://a.espncdn.com/i/teamlogos/leagues/500/nba.png',
  'nhl': 'https://a.espncdn.com/i/teamlogos/leagues/500/nhl.png',
  'mlb': 'https://a.espncdn.com/i/teamlogos/leagues/500/mlb.png',
  'ncaa_f': 'https://a.espncdn.com/i/teamlogos/leagues/500/ncaaf.png',
  'ncaa-f': 'https://a.espncdn.com/i/teamlogos/leagues/500/ncaaf.png',
  'ncaa_b': 'https://a.espncdn.com/i/teamlogos/leagues/500/ncaa.png',
  'ncaa-b': 'https://a.espncdn.com/i/teamlogos/leagues/500/ncaa.png'
};

// Sports that support Top 25 filtering (both formats for compatibility)
const sportsWithRankings = ['ncaa_b', 'ncaa-b', 'ncaa_f', 'ncaa-f'];

const CompactGameFilters = ({
  selectedSport,
  onSportChange,
  selectedDate,
  onDateChange,
  rankingFilter = 'all',  // 'all' or 'top25'
  onRankingFilterChange
}) => {
  const [availableSports, setAvailableSports] = useState([]);
  const initializedRef = useRef(false);

  // Check if current sport supports ranking filter
  const showRankingFilter = sportsWithRankings.includes(selectedSport);

  // Memoize the sport change handler
  const handleInitialSport = useCallback((sports) => {
    if (!initializedRef.current && !selectedSport && sports.length > 0) {
      initializedRef.current = true;
      onSportChange(sports[0].id);
    }
  }, [selectedSport, onSportChange]);

  // Fetch active sports on component mount
  useEffect(() => {
    const fetchActiveSports = async () => {
      try {
        const response = await api.get('/predictions/active-sports');
        if (response.data.success && response.data.sports) {
          const activeSports = response.data.sports.map(sport => ({
            id: sport.sport.replace('_', '-'),  // Convert ncaa_f to ncaa-f for URL-friendly format
            name: sport.name,
            logo: sportLogos[sport.sport] || sportLogos[sport.sport.replace('_', '-')]
          }));
          setAvailableSports(activeSports);
          handleInitialSport(activeSports);
        }
      } catch (error) {
        console.error('Error fetching active sports:', error);
        // Fallback to in-season sports if API fails (no 'all' option)
        const fallbackSports = [
          { id: 'nfl', name: 'NFL', logo: sportLogos['nfl'] },
          { id: 'nba', name: 'NBA', logo: sportLogos['nba'] },
          { id: 'nhl', name: 'NHL', logo: sportLogos['nhl'] },
          { id: 'mlb', name: 'MLB', logo: sportLogos['mlb'] },
          { id: 'ncaa-f', name: 'NCAAF', logo: sportLogos['ncaa-f'] },
          { id: 'ncaa-b', name: 'NCAAB', logo: sportLogos['ncaa-b'] },
        ];
        setAvailableSports(fallbackSports);
        handleInitialSport(fallbackSports);
      }
    };

    fetchActiveSports();
  }, [handleInitialSport]);
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const adjustDate = (days) => {
    const date = new Date(selectedDate + 'T00:00:00');
    date.setDate(date.getDate() + days);
    onDateChange(date.toISOString().split('T')[0]);
  };

  return (
    <div className="glass-card p-4 mb-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Sport Pills */}
        <div className="flex flex-wrap gap-2">
          {availableSports.map((sport) => (
            <button
              key={sport.id}
              onClick={() => onSportChange(sport.id)}
              className={`
                px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200
                flex items-center gap-2
                ${selectedSport === sport.id
                  ? 'bg-accent-blue text-white shadow-lg shadow-accent-blue/50'
                  : 'bg-bg-card hover:bg-bg-card-hover border border-border text-text-secondary hover:text-text-primary'
                }
              `}
            >
              <img
                src={sport.logo}
                alt={`${sport.id} logo`}
                className="w-6 h-6 object-contain"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
              <span>{sport.id.toUpperCase()}</span>
            </button>
          ))}
        </div>

        {/* Compact Date Navigator */}
        <div className="flex items-center gap-2 bg-bg-card border border-border rounded-lg p-1">
          <button
            onClick={() => adjustDate(-1)}
            className="p-2 hover:bg-bg-card-hover rounded-md transition-colors"
            title="Previous day"
          >
            <ChevronLeftIcon className="h-5 w-5 text-text-secondary" />
          </button>

          <div className="relative">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange(e.target.value)}
              className="
                appearance-none bg-transparent border-none
                text-text-primary font-medium text-sm
                cursor-pointer px-2 py-1 rounded
                hover:bg-bg-card-hover transition-colors
                focus:outline-none focus:ring-2 focus:ring-accent-blue/50
              "
              style={{ colorScheme: 'dark' }}
            />
            <CalendarIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary pointer-events-none" />
          </div>

          <button
            onClick={() => adjustDate(1)}
            className="p-2 hover:bg-bg-card-hover rounded-md transition-colors"
            title="Next day"
          >
            <ChevronRightIcon className="h-5 w-5 text-text-secondary" />
          </button>

          <div className="h-6 w-px bg-border mx-1" />

          <button
            onClick={() => {
              const today = new Date();
              onDateChange(today.toISOString().split('T')[0]);
            }}
            className="px-3 py-1.5 rounded-md bg-bg-card-hover hover:bg-accent-blue/10 text-text-secondary hover:text-accent-blue text-sm font-medium transition-all"
          >
            Today
          </button>
        </div>

        {/* Top 25 Filter (for NCAA sports) */}
        {showRankingFilter && onRankingFilterChange && (
          <div className="flex items-center gap-2 bg-bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => onRankingFilterChange('all')}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${rankingFilter === 'all'
                  ? 'bg-accent-blue text-white'
                  : 'hover:bg-bg-card-hover text-text-secondary hover:text-text-primary'
                }
              `}
            >
              All Games
            </button>
            <button
              onClick={() => onRankingFilterChange('top25')}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${rankingFilter === 'top25'
                  ? 'bg-accent-blue text-white'
                  : 'hover:bg-bg-card-hover text-text-secondary hover:text-text-primary'
                }
              `}
            >
              Top 25
            </button>
          </div>
        )}
      </div>

      {/* Selected Date Display */}
      <div className="mt-3 pt-3 border-t border-border">
        <p className="text-xs text-text-secondary">
          Showing games for <span className="text-text-primary font-semibold">{formatDate(selectedDate)}</span>
        </p>
      </div>
    </div>
  );
};

export default CompactGameFilters;
